import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { ShieldCheck, Clock, Search, Sparkles, Loader2, Star, Mail, CheckCircle2, LayoutGrid, BarChart3, Briefcase, Building2, Bookmark, Trash2, MessageSquareText, BookOpen } from "lucide-react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import CandidateCard from "@/components/recruiter/CandidateCard";
import ContactCandidateModal from "@/components/recruiter/ContactCandidateModal";
import InviteInterviewModal from "@/components/recruiter/InviteInterviewModal";
import ScheduleMeetModal from "@/components/recruiter/ScheduleMeetModal";
import BulkMessageModal from "@/components/recruiter/BulkMessageModal";
import PipelineBoard from "@/components/recruiter/PipelineBoard";
import AnalyticsPanel from "@/components/recruiter/AnalyticsPanel";
import JobPostingsPanel from "@/components/recruiter/JobPostingsPanel";
import KnowledgeStudioPanel from "@/components/recruiter/KnowledgeStudioPanel";
import CompanyProfilePanel from "@/components/recruiter/CompanyProfilePanel";

const TRACK_OPTIONS = [
  { id: "", label: "All Tracks" },
  { id: "data_cloud", label: "Data Cloud" },
  { id: "sfmc", label: "SFMC" },
  { id: "mc_next", label: "MC Next" },
];
const EMPTY_FILTERS = { skills: "", min_xp: "", min_case_studies: "", has_certificate: false, track: "", location: "" };

export default function RecruiterDashboardPage() {
  console.log("RENDER_MARKER_RECRUITER_DASHBOARD");
  const { user } = useAuth();
  const [tab, setTab] = useState("search");
  const [candidates, setCandidates] = useState([]);
  const [matches, setMatches] = useState([]);
  const [shortlist, setShortlist] = useState([]);
  const [shortlistIds, setShortlistIds] = useState(new Set());
  const [sentMessages, setSentMessages] = useState([]);
  const [loading, setLoading] = useState(false);
  const [contactTarget, setContactTarget] = useState(null);
  const [interviewTarget, setInterviewTarget] = useState(null);
  const [meetTarget, setMeetTarget] = useState(null);

  const [filters, setFilters] = useState(EMPTY_FILTERS);
  const [jobDescription, setJobDescription] = useState("");
  const [matching, setMatching] = useState(false);

  const [selectedIds, setSelectedIds] = useState(new Set());
  const [bulkMessageOpen, setBulkMessageOpen] = useState(false);
  const [savedSearches, setSavedSearches] = useState([]);
  const [saveSearchName, setSaveSearchName] = useState("");

  const [pipeline, setPipeline] = useState(null);
  const [analytics, setAnalytics] = useState(null);

  const loadShortlist = () => {
    api.get("/recruiter/shortlist").then((r) => {
      setShortlist(r.data);
      setShortlistIds(new Set(r.data.map((c) => c.user_id)));
    });
  };

  const loadPipeline = () => api.get("/recruiter/pipeline").then((r) => setPipeline(r.data));
  const loadAnalytics = () => api.get("/recruiter/analytics").then((r) => setAnalytics(r.data));
  const loadSavedSearches = () => api.get("/recruiter/saved-searches").then((r) => setSavedSearches(r.data));

  useEffect(() => {
    if (user?.recruiter_status !== "approved") return;
    setLoading(true);
    api.get("/recruiter/candidates").then((r) => setCandidates(r.data)).finally(() => setLoading(false));
    loadShortlist();
    api.get("/recruiter/sent-messages").then((r) => setSentMessages(r.data));
    loadSavedSearches();
  }, [user?.recruiter_status]);

  useEffect(() => {
    if (tab === "pipeline") loadPipeline();
    if (tab === "analytics") loadAnalytics();
  }, [tab]);

  const searchCandidates = async (overrideFilters) => {
    const f = overrideFilters || filters;
    setLoading(true);
    try {
      const params = {};
      if (f.skills) params.skills = f.skills;
      if (f.min_xp) params.min_xp = f.min_xp;
      if (f.min_case_studies) params.min_case_studies = f.min_case_studies;
      if (f.has_certificate) params.has_certificate = true;
      if (f.track) params.track = f.track;
      if (f.location) params.location = f.location;
      const { data } = await api.get("/recruiter/candidates", { params });
      setCandidates(data);
      setSelectedIds(new Set());
    } finally {
      setLoading(false);
    }
  };

  const runMatch = async () => {
    if (!jobDescription.trim()) {
      toast.error("Describe the role first");
      return;
    }
    setMatching(true);
    try {
      const { data } = await api.post("/recruiter/match", { job_description: jobDescription });
      setMatches(data);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Matching failed");
    } finally {
      setMatching(false);
    }
  };

  const toggleShortlist = async (candidate) => {
    if (shortlistIds.has(candidate.user_id)) {
      await api.delete(`/recruiter/shortlist/${candidate.user_id}`);
      toast.success("Removed from shortlist");
    } else {
      await api.post(`/recruiter/shortlist/${candidate.user_id}`, {});
      toast.success("Added to shortlist");
    }
    loadShortlist();
  };

  const toggleSelect = (userId) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      next.has(userId) ? next.delete(userId) : next.add(userId);
      return next;
    });
  };

  const bulkShortlistSelected = async () => {
    const { data } = await api.post("/recruiter/bulk/shortlist", { user_ids: Array.from(selectedIds) });
    toast.success(data.message);
    setSelectedIds(new Set());
    loadShortlist();
  };

  const saveCurrentSearch = async () => {
    if (!saveSearchName.trim()) {
      toast.error("Give this search a name");
      return;
    }
    await api.post("/recruiter/saved-searches", { name: saveSearchName.trim(), filters });
    toast.success("Search saved");
    setSaveSearchName("");
    loadSavedSearches();
  };

  const applySavedSearch = (search) => {
    const f = { ...EMPTY_FILTERS, ...search.filters };
    setFilters(f);
    searchCandidates(f);
  };

  const deleteSavedSearch = async (searchId) => {
    await api.delete(`/recruiter/saved-searches/${searchId}`);
    loadSavedSearches();
  };

  if (user?.recruiter_status === "pending") {
    return (
      <div className="max-w-lg mx-auto px-6 py-24 text-center" data-testid="recruiter-pending-approval">
        <Clock className="w-10 h-10 text-amber-400 mx-auto mb-4" />
        <h1 className="font-heading text-2xl font-semibold text-white">Account Pending Approval</h1>
        <p className="text-sm text-slate-400 mt-3">
          Thanks for registering, {user.name}! Your recruiter account for <span className="text-white font-semibold">{user.company_name}</span> is being reviewed by our team.
          You'll get notified once approved and can start searching verified candidates.
        </p>
      </div>
    );
  }

  if (user?.recruiter_status === "rejected") {
    return (
      <div className="max-w-lg mx-auto px-6 py-24 text-center" data-testid="recruiter-rejected">
        <h1 className="font-heading text-2xl font-semibold text-white">Access Not Approved</h1>
        <p className="text-sm text-slate-400 mt-3">Your recruiter account request was not approved. Contact support if you believe this is a mistake.</p>
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 py-8" data-testid="recruiter-dashboard-page">
      <div className="flex items-center gap-3 mb-2">
        <ShieldCheck className="w-6 h-6 text-indigo-400" />
        <span className="text-xs font-bold uppercase tracking-[0.2em] text-indigo-400">Verified Recruiter</span>
      </div>
      <h1 className="font-heading text-3xl font-semibold text-white">{user.company_name}</h1>
      <p className="text-sm text-slate-400 mt-2">Search verified Salesforce Data Cloud talent by skills, XP, projects, and certification.</p>

      <Tabs value={tab} onValueChange={setTab} className="mt-8">
        <TabsList data-testid="recruiter-tabs" className="flex-wrap h-auto">
          <TabsTrigger value="search" data-testid="recruiter-tab-search">Search Candidates</TabsTrigger>
          <TabsTrigger value="match" data-testid="recruiter-tab-match">AI Candidate Matching</TabsTrigger>
          <TabsTrigger value="shortlist" data-testid="recruiter-tab-shortlist">Shortlist ({shortlist.length})</TabsTrigger>
          <TabsTrigger value="pipeline" data-testid="recruiter-tab-pipeline"><LayoutGrid className="w-3.5 h-3.5 mr-1.5" /> Pipeline</TabsTrigger>
          <TabsTrigger value="jobs" data-testid="recruiter-tab-jobs"><Briefcase className="w-3.5 h-3.5 mr-1.5" /> Job Postings</TabsTrigger>
          <TabsTrigger value="knowledge" data-testid="recruiter-tab-knowledge"><BookOpen className="w-3.5 h-3.5 mr-1.5" /> AI Knowledge Studio</TabsTrigger>
          <TabsTrigger value="analytics" data-testid="recruiter-tab-analytics"><BarChart3 className="w-3.5 h-3.5 mr-1.5" /> Analytics</TabsTrigger>
          <TabsTrigger value="company" data-testid="recruiter-tab-company"><Building2 className="w-3.5 h-3.5 mr-1.5" /> Company Profile</TabsTrigger>
          <TabsTrigger value="messages" data-testid="recruiter-tab-messages">Sent Messages ({sentMessages.length})</TabsTrigger>
        </TabsList>

        <TabsContent value="search" className="mt-6">
          <div className="glass-panel rounded-2xl p-5 mb-4 flex flex-wrap items-end gap-3">
            <div>
              <label className="text-[11px] font-bold uppercase tracking-wider text-slate-400">Skills / Keywords</label>
              <input value={filters.skills} onChange={(e) => setFilters((f) => ({ ...f, skills: e.target.value }))} placeholder="e.g. Identity, Retail" data-testid="recruiter-filter-skills" className="mt-1 bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-sm text-white w-40 focus:outline-none focus:ring-1 focus:ring-indigo-400" />
            </div>
            <div>
              <label className="text-[11px] font-bold uppercase tracking-wider text-slate-400">Track</label>
              <select value={filters.track} onChange={(e) => setFilters((f) => ({ ...f, track: e.target.value }))} data-testid="recruiter-filter-track" className="mt-1 bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-sm text-white w-32 focus:outline-none focus:ring-1 focus:ring-indigo-400">
                {TRACK_OPTIONS.map((t) => <option key={t.id} value={t.id} className="bg-[#0f1522]">{t.label}</option>)}
              </select>
            </div>
            <div>
              <label className="text-[11px] font-bold uppercase tracking-wider text-slate-400">Location</label>
              <input value={filters.location} onChange={(e) => setFilters((f) => ({ ...f, location: e.target.value }))} placeholder="e.g. India" data-testid="recruiter-filter-location" className="mt-1 bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-sm text-white w-28 focus:outline-none focus:ring-1 focus:ring-indigo-400" />
            </div>
            <div>
              <label className="text-[11px] font-bold uppercase tracking-wider text-slate-400">Min XP</label>
              <input type="number" value={filters.min_xp} onChange={(e) => setFilters((f) => ({ ...f, min_xp: e.target.value }))} placeholder="0" data-testid="recruiter-filter-min-xp" className="mt-1 bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-sm text-white w-24 focus:outline-none focus:ring-1 focus:ring-indigo-400" />
            </div>
            <div>
              <label className="text-[11px] font-bold uppercase tracking-wider text-slate-400">Min Case Studies</label>
              <input type="number" value={filters.min_case_studies} onChange={(e) => setFilters((f) => ({ ...f, min_case_studies: e.target.value }))} placeholder="0" data-testid="recruiter-filter-min-case-studies" className="mt-1 bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-sm text-white w-24 focus:outline-none focus:ring-1 focus:ring-indigo-400" />
            </div>
            <label className="flex items-center gap-2 text-sm text-slate-300 pb-2">
              <input type="checkbox" checked={filters.has_certificate} onChange={(e) => setFilters((f) => ({ ...f, has_certificate: e.target.checked }))} data-testid="recruiter-filter-has-certificate" className="accent-indigo-400" />
              Certified only
            </label>
            <button onClick={() => searchCandidates()} disabled={loading} data-testid="recruiter-search-btn" className="flex items-center gap-2 bg-primary hover:bg-primary/90 disabled:opacity-60 text-white text-sm font-semibold px-4 py-2 rounded-lg">
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />} Search
            </button>
          </div>

          <div className="flex flex-wrap items-center gap-2 mb-4">
            <div className="flex items-center gap-1.5">
              <input value={saveSearchName} onChange={(e) => setSaveSearchName(e.target.value)} placeholder="Name this search..." data-testid="recruiter-save-search-name-input" className="bg-white/5 border border-white/10 rounded-lg px-2.5 py-1.5 text-xs text-white w-40 focus:outline-none focus:ring-1 focus:ring-indigo-400" />
              <button onClick={saveCurrentSearch} data-testid="recruiter-save-search-btn" className="flex items-center gap-1 text-xs font-semibold text-slate-300 hover:text-white bg-white/5 hover:bg-white/10 px-2.5 py-1.5 rounded-lg"><Bookmark className="w-3 h-3" /> Save</button>
            </div>
            {savedSearches.map((s) => (
              <span key={s.id} className="flex items-center gap-1.5 text-xs font-semibold text-indigo-300 bg-indigo-400/10 border border-indigo-400/20 rounded-full pl-3 pr-1.5 py-1" data-testid={`recruiter-saved-search-${s.id}`}>
                <button onClick={() => applySavedSearch(s)}>{s.name}</button>
                <button onClick={() => deleteSavedSearch(s.id)} data-testid={`recruiter-saved-search-delete-${s.id}`}><Trash2 className="w-3 h-3" /></button>
              </span>
            ))}
          </div>

          {selectedIds.size > 0 && (
            <div className="glass-panel rounded-xl p-3 mb-4 flex items-center gap-3" data-testid="recruiter-bulk-toolbar">
              <span className="text-xs font-semibold text-white">{selectedIds.size} selected</span>
              <button onClick={bulkShortlistSelected} data-testid="recruiter-bulk-shortlist-btn" className="flex items-center gap-1.5 text-xs font-semibold text-[#FBBF24] bg-[#FBBF24]/10 hover:bg-[#FBBF24]/20 px-3 py-1.5 rounded-lg"><Star className="w-3 h-3" /> Shortlist Selected</button>
              <button onClick={() => setBulkMessageOpen(true)} data-testid="recruiter-bulk-message-btn" className="flex items-center gap-1.5 text-xs font-semibold text-indigo-300 bg-indigo-400/10 hover:bg-indigo-400/20 px-3 py-1.5 rounded-lg"><MessageSquareText className="w-3 h-3" /> Message Selected</button>
              <button onClick={() => setSelectedIds(new Set())} className="text-xs text-slate-400 hover:text-white ml-auto">Clear</button>
            </div>
          )}

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4" data-testid="recruiter-candidates-grid">
            {candidates.map((c) => (
              <CandidateCard
                key={c.user_id} candidate={c} isShortlisted={shortlistIds.has(c.user_id)} onToggleShortlist={toggleShortlist}
                onContact={setContactTarget} onInviteInterview={setInterviewTarget} onScheduleMeet={setMeetTarget}
                selectable selected={selectedIds.has(c.user_id)} onToggleSelect={toggleSelect}
              />
            ))}
            {!loading && candidates.length === 0 && <p className="text-sm text-slate-400 col-span-2 text-center py-10" data-testid="recruiter-no-candidates">No candidates match these filters yet.</p>}
          </div>
        </TabsContent>

        <TabsContent value="match" className="mt-6">
          <div className="glass-panel rounded-2xl p-5 mb-6">
            <label className="text-[11px] font-bold uppercase tracking-wider text-slate-400">Job Description</label>
            <textarea
              value={jobDescription} onChange={(e) => setJobDescription(e.target.value)} rows={4}
              placeholder="Paste the role description — required skills, seniority, industry focus..."
              data-testid="recruiter-match-job-description-input"
              className="w-full mt-1.5 bg-white/5 border border-white/10 rounded-lg px-3 py-2.5 text-sm text-white placeholder:text-slate-400 focus:outline-none focus:ring-1 focus:ring-indigo-400 resize-none"
            />
            <button onClick={runMatch} disabled={matching} data-testid="recruiter-run-match-btn" className="mt-3 flex items-center gap-2 bg-gradient-to-r from-purple-500 to-indigo-500 hover:opacity-90 disabled:opacity-60 text-white text-sm font-semibold px-4 py-2.5 rounded-lg">
              {matching ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />} Find Best Matches with AI
            </button>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4" data-testid="recruiter-matches-grid">
            {matches.map((c) => (
              <CandidateCard key={c.user_id} candidate={c} isShortlisted={shortlistIds.has(c.user_id)} onToggleShortlist={toggleShortlist} onContact={setContactTarget} onInviteInterview={setInterviewTarget} onScheduleMeet={setMeetTarget} />
            ))}
          </div>
        </TabsContent>

        <TabsContent value="shortlist" className="mt-6">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4" data-testid="recruiter-shortlist-grid">
            {shortlist.map((c) => (
              <CandidateCard key={c.user_id} candidate={c} isShortlisted={true} onToggleShortlist={toggleShortlist} onContact={setContactTarget} onInviteInterview={setInterviewTarget} onScheduleMeet={setMeetTarget} />
            ))}
            {shortlist.length === 0 && (
              <p className="text-sm text-slate-400 col-span-2 text-center py-10 flex flex-col items-center gap-2" data-testid="recruiter-empty-shortlist">
                <Star className="w-6 h-6 text-slate-400" /> No candidates shortlisted yet.
              </p>
            )}
          </div>
        </TabsContent>

        <TabsContent value="pipeline" className="mt-6">
          {pipeline ? (
            <PipelineBoard board={pipeline} onRefresh={loadPipeline} onContact={setContactTarget} />
          ) : (
            <Loader2 className="w-6 h-6 animate-spin text-slate-400 mx-auto my-10" />
          )}
        </TabsContent>

        <TabsContent value="jobs" className="mt-6">
          <JobPostingsPanel />
        </TabsContent>

        <TabsContent value="knowledge" className="mt-6">
          <KnowledgeStudioPanel />
        </TabsContent>

        <TabsContent value="analytics" className="mt-6">
          {analytics ? <AnalyticsPanel analytics={analytics} /> : <Loader2 className="w-6 h-6 animate-spin text-slate-400 mx-auto my-10" />}
        </TabsContent>

        <TabsContent value="company" className="mt-6">
          <CompanyProfilePanel />
        </TabsContent>

        <TabsContent value="messages" className="mt-6">
          <div className="space-y-3" data-testid="recruiter-sent-messages-list">
            {sentMessages.map((m) => (
              <div key={m.id} className="glass-panel rounded-xl p-4" data-testid={`recruiter-sent-message-${m.id}`}>
                <div className="flex items-center gap-2 mb-1.5">
                  <Mail className="w-3.5 h-3.5 text-indigo-400" />
                  <p className="text-sm font-semibold text-white">To: {m.learner_name}</p>
                  {m.interview_invite && <span className="text-[10px] font-bold uppercase bg-purple-400/15 text-purple-300 px-1.5 py-0.5 rounded-full">Interview Invite</span>}
                </div>
                <p className="text-sm text-slate-300">{m.message}</p>
                {m.reply_text ? (
                  <div className="mt-3 pt-3 border-t border-white/10 flex items-start gap-2" data-testid={`recruiter-message-reply-${m.id}`}>
                    <CheckCircle2 className="w-3.5 h-3.5 text-emerald-400 mt-0.5 shrink-0" />
                    <div>
                      <p className="text-xs text-emerald-400 font-semibold">Candidate replied</p>
                      <p className="text-sm text-slate-300 mt-1">{m.reply_text}</p>
                    </div>
                  </div>
                ) : (
                  <p className="text-xs text-slate-500 mt-2">Awaiting reply...</p>
                )}
              </div>
            ))}
            {sentMessages.length === 0 && (
              <p className="text-sm text-slate-400 text-center py-10" data-testid="recruiter-no-sent-messages">No messages sent yet.</p>
            )}
          </div>
        </TabsContent>

      </Tabs>

      {contactTarget && <ContactCandidateModal candidate={contactTarget} onClose={() => setContactTarget(null)} />}
      {interviewTarget && <InviteInterviewModal candidate={interviewTarget} onClose={() => setInterviewTarget(null)} />}
      {meetTarget && <ScheduleMeetModal candidate={meetTarget} onClose={() => setMeetTarget(null)} />}
      {bulkMessageOpen && (
        <BulkMessageModal
          userIds={Array.from(selectedIds)} onClose={() => setBulkMessageOpen(false)}
          onDone={() => setSelectedIds(new Set())}
        />
      )}
    </div>
  );
}
